import { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Loader2, CheckCircle2, Mail, KeyRound, Eye, EyeOff } from 'lucide-react';
import AuthShell from './AuthShell';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/retreat/users/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === 'string' ? d : 'Something went wrong — please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell>
      <form onSubmit={submit} className="space-y-5" data-testid="forgot-password-form">
        <div>
          <p className="eyebrow text-white/40">Reset Password</p>
          <h1 className="font-display text-3xl text-white mt-1">Forgot your password?</h1>
          <p className="text-sm text-white/50 mt-2 leading-relaxed">
            Enter the email you registered with and we&apos;ll send you a reset link.
          </p>
        </div>
        {sent ? (
          <div className="rounded-xl border border-emerald-400/30 bg-emerald-500/10 p-5 text-center" data-testid="forgot-password-sent">
            <CheckCircle2 className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
            <p className="text-sm text-white/80 leading-relaxed">
              If that email is on file, a reset link is on its way. Check your inbox (and spam folder).
              The link expires in 1 hour.
            </p>
          </div>
        ) : (
          <>
            <div className="relative">
              <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
              <Input
                type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="h-12 pl-10 bg-white/5 border-white/15 text-white placeholder:text-white/30"
                data-testid="forgot-password-email"
              />
            </div>
            {error && <p className="text-xs text-red-300 pl-1" data-testid="forgot-password-error">{error}</p>}
            <Button type="submit" disabled={loading}
              className="w-full h-12 bg-gradient-to-r from-wine to-burgundy hover:from-wine-600 hover:to-burgundy-600 text-white font-semibold text-base"
              data-testid="forgot-password-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send Reset Link'}
            </Button>
          </>
        )}
        <p className="text-center text-xs text-white/40">
          <Link to="/login" className="text-white/70 hover:text-white underline underline-offset-4" data-testid="back-to-login-link">
            Back to sign in
          </Link>
        </p>
      </form>
    </AuthShell>
  );
};

export const ResetPassword = () => {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [status, setStatus] = useState('checking'); // checking | valid | invalid | done
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const verify = useCallback(async () => {
    if (!token) { setStatus('invalid'); return; }
    try {
      const res = await axios.get(`${BACKEND_URL}/api/retreat/users/reset-password/verify/${token}`);
      if (res.data.valid) { setEmail(res.data.email || ''); setStatus('valid'); }
      else setStatus('invalid');
    } catch {
      setStatus('invalid');
    }
  }, [token]);

  useEffect(() => { verify(); }, [verify]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
    if (password !== confirm) { setError('Passwords do not match'); return; }
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/retreat/users/reset-password`, { token, password });
      setStatus('done');
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === 'string' ? d : 'Something went wrong — please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell>
      <div className="space-y-5" data-testid="reset-password-page">
        <div>
          <p className="eyebrow text-white/40">Reset Password</p>
          <h1 className="font-display text-3xl text-white mt-1">Choose a new password</h1>
          {email && status === 'valid' && <p className="text-sm text-white/50 mt-2">for {email}</p>}
        </div>

        {status === 'checking' && (
          <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-white/50" /></div>
        )}

        {status === 'invalid' && (
          <div className="rounded-xl border border-red-400/30 bg-red-500/10 p-5 text-center" data-testid="reset-invalid-notice">
            <p className="text-sm text-white/80 leading-relaxed">
              This reset link is invalid or has expired.
            </p>
            <Link to="/forgot-password" className="inline-block mt-3 text-sm text-white underline underline-offset-4" data-testid="request-new-link">
              Request a new link
            </Link>
          </div>
        )}

        {status === 'valid' && (
          <form onSubmit={submit} className="space-y-4" data-testid="reset-password-form">
            <div className="relative">
              <KeyRound className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
              <Input type={showPw ? 'text' : 'password'} required value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="New password (min 6 characters)"
                className="h-12 pl-10 pr-11 bg-white/5 border-white/15 text-white placeholder:text-white/30"
                data-testid="reset-password-input" />
              <button type="button" onClick={() => setShowPw(!showPw)}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70"
                data-testid="reset-password-toggle">
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <div className="relative">
              <KeyRound className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
              <Input type={showPw ? 'text' : 'password'} required value={confirm} onChange={(e) => setConfirm(e.target.value)}
                placeholder="Confirm new password"
                className="h-12 pl-10 bg-white/5 border-white/15 text-white placeholder:text-white/30"
                data-testid="reset-confirm-input" />
            </div>
            {error && <p className="text-xs text-red-300 pl-1" data-testid="reset-password-error">{error}</p>}
            <Button type="submit" disabled={loading}
              className="w-full h-12 bg-gradient-to-r from-wine to-burgundy hover:from-wine-600 hover:to-burgundy-600 text-white font-semibold text-base"
              data-testid="reset-password-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Set New Password'}
            </Button>
          </form>
        )}

        {status === 'done' && (
          <div className="rounded-xl border border-emerald-400/30 bg-emerald-500/10 p-5 text-center" data-testid="reset-success-notice">
            <CheckCircle2 className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
            <p className="text-sm text-white/80">Password updated. You can sign in now.</p>
            <Link to="/login" className="inline-block mt-3 text-sm text-white underline underline-offset-4" data-testid="go-to-login-link">
              Go to sign in
            </Link>
          </div>
        )}
      </div>
    </AuthShell>
  );
};
